import { Box, Button, Card, Chip, Divider, Stack, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { memo, ReactElement, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { HeaderBar, LanguageSelector } from '../Header';
import { selfServiceRoutes } from '../utils';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { SelfServiceSession, useSelfServiceSession } from '../../../api/queries/selfService/selfServiceSession';
import { Layout } from '../Layout';
import SessionLoadingScreen from '../SessionLoadingScreen';

export function SessionInfopoint(): ReactElement {
  const { jobsiteId, sessionId } = useParams<{ jobsiteId: string; sessionId: string }>();
  const navigate = useNavigate();
  const { data: session } = useSelfServiceSession({ jobsiteId: jobsiteId!, sessionId: sessionId! }); // Router ensures it is defined

  const handleNavigateToSession = useCallback(
    function handleNavigateToSession() {
      navigate(selfServiceRoutes.sessionMain(jobsiteId!, sessionId!));
    },
    [navigate, jobsiteId, sessionId]
  );

  if (!session) return <SessionLoadingScreen />;

  return (
    <Layout
      header={<HeaderBar languageSelector={<LanguageSelector />} />}
      content={<SessionInfoContent session={session} onNavigateToSession={handleNavigateToSession} />}
    />
  );
}

interface SessionInfoContentProps {
  session: SelfServiceSession;
  onNavigateToSession: () => void;
}

function SessionInfoContent({ session, onNavigateToSession }: SessionInfoContentProps): ReactElement {
  const { t } = useTranslation();

  const statusText = useMemo(() => {
    switch (session.status) {
      case 'active':
        return t('selfService.infopoint.status.active');
      case 'completed':
        return t('selfService.infopoint.status.completed');
      case 'expired':
        return t('selfService.infopoint.status.expired');
      case 'cancelled':
        return t('selfService.infopoint.status.cancelled');
      case 'failed':
        return t('selfService.infopoint.status.failed');
      default:
        return session.status;
    }
  }, [session.status, t]);

  const statusColor = useMemo(() => {
    switch (session.status) {
      case 'active':
        return 'primary';
      case 'completed':
        return 'success';
      case 'expired':
        return 'warning';
      case 'cancelled':
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  }, [session.status]) as 'success' | 'primary' | 'warning' | 'error' | 'default';

  const getTimeSinceCreated = useCallback(
    function getTimeSinceCreated(): string {
      const now = DateTime.now();
      const diff = now.diff(session.sessionStartedAt, ['hours', 'minutes']).toObject();
      const hours = Math.floor(diff.hours ?? 0);
      const minutes = Math.floor(diff.minutes ?? 0);

      if (hours > 0) {
        return t('selfService.infopoint.timeSince.withHours', { hours, minutes });
      } else {
        return t('selfService.infopoint.timeSince.minutesOnly', { minutes });
      }
    },
    [session.sessionStartedAt, t]
  );

  return (
    <Box padding={2} height='100%' boxSizing='border-box' display='flex' flexDirection='column' gap={2}>
      <Card
        sx={{
          height: '100%',
          width: '100%',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          padding: 2,
          borderRadius: '10px',
        }}
        elevation={4}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Typography variant='h5' component='h1'>
            {t('selfService.infopoint.title')}
          </Typography>
          <Chip label={statusText} color={statusColor} variant='filled' />
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <SessionInfoField
            title={t('selfService.infopoint.sessionId')}
            content={session.sessionId}
            contentProps={{ sx: { fontFamily: 'monospace' } }}
          />

          <SessionInfoField
            title={t('selfService.infopoint.createdAt')}
            content={`${session.sessionStartedAt.toLocaleString(DateTime.DATETIME_MED)} (${getTimeSinceCreated()})`}
          />

          {session.extended > 0 && (
            <SessionInfoField
              title={t('selfService.infopoint.extensions')}
              content={t('selfService.infopoint.extensionCount', { count: session.extended })}
            />
          )}

          {session.status === 'expired' && (
            <SessionInfoField
              title={t('selfService.infopoint.expiredAt')}
              content={session.sessionExpiresAt.toLocaleString(DateTime.DATETIME_MED)}
            />
          )}

          {session.status === 'active' && (
            <SessionInfoField
              title={t('selfService.infopoint.expiresAt')}
              content={session.sessionExpiresAt.toLocaleString(DateTime.DATETIME_MED)}
            />
          )}
        </Box>

        {session.status === 'active' && (
          <Box marginTop='auto'>
            <Button variant='contained' color='primary' onClick={onNavigateToSession} fullWidth>
              {t('selfService.infopoint.goToActiveSession')}
            </Button>
          </Box>
        )}
        {session.status === 'completed' && (
          <>
            <Divider />
            <Stack alignItems='center' alignSelf='center' justifyContent='center' gap='1rem' flex={1}>
              <DoneAllIcon color='success' fontSize='large' />
              <Typography variant='h5' fontWeight='bold' align='center'>
                {t('selfService.infopoint.completedTitle')}
              </Typography>
              <Typography variant='h6'>{t('selfService.infopoint.completedHint')}</Typography>
            </Stack>
          </>
        )}
      </Card>
    </Box>
  );
}

interface SessionInfoFieldProps {
  title: string;
  content: string;
  contentProps?: object;
}

const SessionInfoField = memo(function SessionInfoField({
  title,
  content,
  contentProps,
}: SessionInfoFieldProps): ReactElement {
  return (
    <Box>
      <Typography variant='subtitle2' color='text.secondary'>
        {title}
      </Typography>
      <Typography variant='body1' {...contentProps}>
        {content}
      </Typography>
    </Box>
  );
});
